import React, { useEffect } from "react";
import {
  Box,
  Typography,
  Divider,
  useTheme,
  CircularProgress,
} from "@mui/material";
import DOMPurify from "dompurify";
import {
  useLazyGetNotificationDetailQuery,
  useMarkAsReadMutation,
} from "../../features/api/notificationApiSlice";

const MessageDetail = ({ messageId }) => {
  const theme = useTheme();

  const [fetchDetail, { data: detailData, isLoading, isFetching }] =
    useLazyGetNotificationDetailQuery();
  const [markAsRead] = useMarkAsReadMutation();

  const message = detailData?.result;

  useEffect(() => {
    if (messageId) {
      fetchDetail(messageId);
    }
  }, [messageId, fetchDetail]);

  // Mark as read when message is loaded and is unread
  useEffect(() => {
    if (message && !message.isRead) {
      markAsRead(messageId);
    }
  }, [message, messageId, markAsRead]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }) + " " + date.toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateStr;
    }
  };

  if (isLoading || isFetching) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!message) {
    return (
      <Box py={4} textAlign="center">
        <Typography
          variant="body2"
          sx={{ color: process.env.REACT_APP_COLOR_TEXT || "white" }}
        >
          Message not found.
        </Typography>
      </Box>
    );
  }

  return (
    <Box px={2} py={1}>
      {/* Subject and date */}
      <Box mb={2}>
        <Typography
          variant="h6"
          sx={{
            color: process.env.REACT_APP_COLOR_TEXT || "white",
            fontWeight: 600,
            fontSize: "16px",
            lineHeight: 1.4,
          }}
        >
          {message.subject}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: process.env.REACT_APP_COLOR_TEXT || "white",
            opacity: 0.6,
            fontSize: "12px",
          }}
        >
          {formatDate(message.date)}
        </Typography>
      </Box>

      <Divider sx={{ borderColor: theme.palette.divider1 || "#444", mb: 2 }} />

      {message.imageUrl && (
        <Box mb={2}>
          <Box
            component="img"
            src={message.imageUrl}
            alt={message.subject}
            sx={{
              width: "100%",
              objectFit: "contain",
              borderRadius: "8px",
            }}
          />
        </Box>
      )}

      {/* Message content (sanitized HTML) */}
      <Box
        sx={{
          color: process.env.REACT_APP_COLOR_TEXT || "white",
          fontSize: "14px",
          lineHeight: 1.8,
          "& a": {
            color: theme.palette.primary.main,
            textDecoration: "underline",
          },
          "& img": {
            maxWidth: "100%",
            height: "auto",
          },
          "& p": {
            marginBottom: "8px",
          },
        }}
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(message.content || ""),
        }}
      />
    </Box>
  );
};

export default MessageDetail;
